import { AppConfig } from "../AppConfig";
import { LighthouseData, SectorColor } from "./lighthouseConstants";
import { hasSameColorConsecutiveSectors, satisfiesIALA } from "./sectorRules";
import { NumericDraft, parseStrictNumber, sameLengthInMeters, SectorDraft, sectorDraftsToSectors } from "./sectorDraft";

export interface SectorFinding {
    severity: "error" | "warning";
    message: string;
    /** Omitted for table-level findings. */
    rowKey?: string;
    field?: keyof SectorDraft;
}

export const hasBlockingErrors = (findings: SectorFinding[]) =>
    findings.some((finding) => finding.severity === "error");

const error = (message: string, rowKey?: string, field?: keyof SectorDraft): SectorFinding => ({
    severity: "error",
    message,
    rowKey,
    field,
});

const angleFindings = (raw: string, rowKey: string, field: keyof SectorDraft): SectorFinding[] => {
    if (raw.trim() === "") return [error("Må fylles ut", rowKey, field)];
    const parsed = parseStrictNumber(raw);
    if (parsed === null) return [error("Ugyldig tall", rowKey, field)];
    if (parsed < 0 || parsed > 360) return [error("Vinkel må være mellom 0 og 360", rowKey, field)];
    return [];
};

const lengthFindings = (draft: NumericDraft, rowKey: string, field: keyof SectorDraft): SectorFinding[] => {
    if (draft.raw.trim() === "") return [error("Må fylles ut", rowKey, field)];
    if (draft.meters === null) return [error("Ugyldig tall", rowKey, field)];
    const { MinSectorLineLength: min, MaxSectorLineLength: max } = AppConfig.Lighthouse;
    if (draft.meters < min || draft.meters > max) {
        return [error(`Lengde må være mellom ${min} og ${max}`, rowKey, field)];
    }
    return [];
};

const radiusFindings = (draft: NumericDraft, rowKey: string): SectorFinding[] => {
    if (draft.raw.trim() === "") return [error("Må fylles ut", rowKey, "innerRadius")];
    if (draft.meters === null) return [error("Ugyldig tall", rowKey, "innerRadius")];
    if (draft.meters < 0) return [error("Radius kan ikke være negativ", rowKey, "innerRadius")];
    return [];
};

/**
 * Pass 1: every field of one row, in isolation. A dark sector is the absence of light, so only
 * its angles mean anything - it has no radius, and no boundary line of its own to give a length
 * to. Its start length is taken from the lit sector before it when the drafts are converted.
 */
const validateRow = (draft: SectorDraft): SectorFinding[] =>
    draft.color === SectorColor.DARK
        ? [
              ...angleFindings(draft.fromAngle, draft.key, "fromAngle"),
              ...angleFindings(draft.toAngle, draft.key, "toAngle"),
          ]
        : [
              ...angleFindings(draft.fromAngle, draft.key, "fromAngle"),
              ...angleFindings(draft.toAngle, draft.key, "toAngle"),
              ...lengthFindings(draft.fromLineLength, draft.key, "fromLineLength"),
              ...lengthFindings(draft.toLineLength, draft.key, "toLineLength"),
              ...radiusFindings(draft.innerRadius, draft.key),
          ];

/** Degrees swept from `from` to `to`, going clockwise. A full circle stays 360, but a
 *  zero-width sector (`from === to`) stays 0 so the sub-1° rule catches it. */
const extent = (from: number, to: number) => {
    const swept = (to - from + 360) % 360;
    return swept === 0 && to !== from ? 360 : swept;
};

const sameBearing = (a: number, b: number) => Math.abs(((a - b + 540) % 360) - 180) < 0.001;

export const validateSectorDrafts = (drafts: SectorDraft[]): SectorFinding[] => {
    if (drafts.length === 0) return [error("Tabellen må ha minst én sektor")];

    const rowFindings = drafts.flatMap(validateRow);

    // Pass 2 needs every row parsed. Dropping only the bad rows would leave their neighbours
    // looking adjacent and fabricate a gap the user never typed, so a single bad cell defers
    // all cross-row rules until it is fixed.
    if (rowFindings.length > 0) return rowFindings;

    const findings: SectorFinding[] = [];
    const ordered = [...drafts].sort((a, b) => parseStrictNumber(a.fromAngle)! - parseStrictNumber(b.fromAngle)!);
    // Messages must number sectors the way the table renders them (drafts order), not the way
    // they were sorted for the angle walk, or the sentence would name the wrong rows.
    const displayNumber = (draft: SectorDraft) => drafts.indexOf(draft) + 1;

    // A Sector carries only its start angle - its end is wherever the next one starts - so a
    // table of one row is a full circle however its `to` was typed, and the `to` is dropped on
    // save. That makes the lone row its own closing neighbour, which the cyclic walk below
    // cannot express: it would compare the row against itself and read a gap as an overlap.
    const isLone = ordered.length === 1;

    for (const [index, draft] of ordered.entries()) {
        const from = parseStrictNumber(draft.fromAngle)!;
        const to = parseStrictNumber(draft.toAngle)!;

        // `from` and `to` on one bearing is a zero-width sector in a table of several, but the
        // all-round light the user meant when the sector is the only one.
        if ((isLone && sameBearing(from, to) ? 360 : extent(from, to)) < 1) {
            findings.push(error("Sektor mindre enn 1 grad", draft.key, "toAngle"));
        }

        if (isLone) {
            // Closure against itself: anything short of the whole circle leaves an arc that
            // nothing covers, and a lone sector has no neighbour it could overlap instead.
            if (!sameBearing(to, from)) {
                findings.push(
                    error(
                        `Hull mellom ${to}° og ${from}° er ikke dekket – legg inn en mørk sektor`,
                        draft.key,
                        "toAngle"
                    )
                );
            }

            // Its start and end line are the same line, and only the start length is stored -
            // so a differing end length would be silently discarded. Flagged on both cells,
            // but with its own wording: the shared-boundary message below would send the user
            // looking for a second sector that does not exist.
            if (
                draft.color !== SectorColor.DARK &&
                !sameLengthInMeters(draft.fromLineLength.meters, draft.toLineLength.meters)
            ) {
                const message =
                    "En rundtlysende sektor har bare én grenselinje – «Lengde fra» og «Lengde til» må være like";
                findings.push(error(message, draft.key, "fromLineLength"), error(message, draft.key, "toLineLength"));
            }
            continue;
        }

        // Cyclic: the last row's `to` is checked against the first row's `from`, which is what
        // makes a separate "circle not closed" rule unnecessary.
        const next = ordered[(index + 1) % ordered.length];

        const nextFrom = parseStrictNumber(next.fromAngle)!;
        if (!sameBearing(to, nextFrom)) {
            findings.push(
                extent(from, to) < extent(from, nextFrom)
                    ? error(
                          `Hull mellom ${to}° og ${nextFrom}° er ikke dekket – legg inn en mørk sektor`,
                          draft.key,
                          "toAngle"
                      )
                    : error(
                          `Sektor ${displayNumber(draft)} og ${displayNumber(next)} overlapper mellom ` +
                              `${nextFrom}° og ${to}°`,
                          draft.key,
                          "toAngle"
                      )
            );
        }

        // Two lit sectors own the same boundary line, so both cells are flagged and each names
        // the value it has to match - one message on one side leaves the user hunting for the
        // other. A boundary onto a dark sector is not shared: the lit side's length is the only
        // one stored for it, and the dark side has no length at all.
        const acrossDarkSector = draft.color === SectorColor.DARK || next.color === SectorColor.DARK;
        if (!acrossDarkSector && !sameLengthInMeters(draft.toLineLength.meters, next.fromLineLength.meters)) {
            findings.push(
                error(
                    `Må være lik «Lengde fra» i sektor ${displayNumber(next)} (${next.fromLineLength.raw})`,
                    draft.key,
                    "toLineLength"
                ),
                error(
                    `Må være lik «Lengde til» i sektor ${displayNumber(draft)} (${draft.toLineLength.raw})`,
                    next.key,
                    "fromLineLength"
                )
            );
        }
    }

    // Delegated to the rules the sector editor already enforces, so the verdicts agree.
    // Both take sectors, so the drafts are converted first. `hasSameColorConsecutiveSectors`
    // returns a bare boolean, so the message cannot name which two sectors clash.
    const sectors = sectorDraftsToSectors(ordered);

    if (hasSameColorConsecutiveSectors(sectors)) {
        findings.push(error("To sektorer ved siden av hverandre har samme farge"));
    }

    // satisfiesIALA reads only `sectors`; the rest of LighthouseData is not consulted.
    if (!satisfiesIALA({ id: "", sectors, setState: undefined } as unknown as LighthouseData)) {
        findings.push({
            severity: "warning",
            message: "Følger ikke IALA-anbefalingen: en hvit sektor bør ha grønn til styrbord og rød til babord",
        });
    }

    return findings;
};
